import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminAuthAPI from '../../api/admin/auth.js';
import AdminTenantAPI from '../../api/admin/tenants.js';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import './AdminDashboard.css';

/**
 * Admin Dashboard
 * System overview and navigation hub for administrators
 * 
 * Created: July 9, 2025
 * Purpose: Provide comprehensive system monitoring and quick access to admin functions
 */
const AdminDashboard = () => {
  const [systemStats, setSystemStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load system overview
      const overview = await AdminTenantAPI.getSystemOverview();
      console.log('Dashboard overview data:', overview); // Debug log
      setSystemStats(overview.stats); // Access the stats property from the response

      // Load recent admin activity
      const activityLogs = await AdminTenantAPI.getAdminActivityLogs({
        limit: 5,
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      setRecentActivity(activityLogs.logs || []);

    } catch (err) {
      console.error('Dashboard load error:', err);

      // Handle session expiration specifically
      if (err.message?.includes('session expired')) {
        navigate('/admin/login');
        return;
      }

      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const refreshDashboard = () => {
    loadDashboardData();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-dashboard-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-dashboard-error">
          <div className="error-card">
            <h3>⚠️ Error Loading Dashboard</h3>
            <p>{error}</p>
            <button onClick={refreshDashboard} className="retry-button">
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-dashboard">
        <div className="dashboard-header">
          <h1>System Dashboard</h1>
          <div className="dashboard-actions">
            <button onClick={refreshDashboard} className="refresh-button">
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* System Statistics */}
        {systemStats && (
          <div className="stats-grid">
            <div className="stat-card tenants">
              <div className="stat-header">
                <h3>Tenants</h3>
                <span className="stat-icon">🏢</span>
              </div>
              <div className="stat-value">{systemStats.total_tenants}</div>
              <div className="stat-subtitle">
                {systemStats.active_tenants} active
              </div>
            </div>

            <div className="stat-card users">
              <div className="stat-header">
                <h3>Users</h3>
                <span className="stat-icon">👥</span>
              </div>
              <div className="stat-value">{systemStats.total_users}</div>
              <div className="stat-subtitle">
                {systemStats.active_users} active
              </div>
            </div>

            <div className="stat-card auth">
              <div className="stat-header">
                <h3>Authentication</h3>
                <span className="stat-icon">🔐</span>
              </div>
              <div className="stat-value">{systemStats.stytch_users}</div>
              <div className="stat-subtitle">
                Stytch users ({systemStats.legacy_users} legacy)
              </div>
            </div>

            <div className="stat-card onboarding">
              <div className="stat-header">
                <h3>Onboarding</h3>
                <span className="stat-icon">📝</span>
              </div>
              <div className="stat-value">{systemStats.total_onboarding_sessions}</div>
              <div className="stat-subtitle">
                Total sessions
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="dashboard-actions-grid">
          <div className="action-card" onClick={() => navigate('/admin/tenants')}>
            <div className="action-icon">🏢</div>
            <h3>Manage Tenants</h3>
            <p>View and manage all tenant accounts</p>
          </div>

          <div className="action-card" onClick={() => navigate('/admin/provision-tenant')}>
            <div className="action-icon">➕</div>
            <h3>Create New Tenant</h3>
            <p>Provision complete tenant with Stytch & Stripe</p>
          </div>

          <div className="action-card" onClick={() => navigate('/admin/migrations')}>
            <div className="action-icon">🔄</div>
            <h3>Migration Tools</h3>
            <p>Manage authentication migrations</p>
          </div>

          <div className="action-card" onClick={() => navigate('/admin/impersonation')}>
            <div className="action-icon">👤</div>
            <h3>User Impersonation</h3>
            <p>Impersonate users for support</p>
          </div>

          <div className="action-card" onClick={() => navigate('/admin/activity')}>
            <div className="action-icon">📊</div>
            <h3>Activity Logs</h3>
            <p>View system activity and audit logs</p>
          </div>

          <div className="action-card monitoring" onClick={() => navigate('/admin/monitoring')}>
            <div className="action-icon">📡</div>
            <h3>System Monitoring</h3>
            <p>Real-time performance and health monitoring</p>
          </div>

          <div className="action-card" onClick={() => navigate('/admin/sync')}>
            <div className="action-icon">🔄</div>
            <h3>Sync Management</h3>
            <p>Manage tenant data synchronization with Pabau</p>
          </div>
        </div>

        {/* Recent Activity */}
        {recentActivity.length > 0 && (
          <div className="recent-activity-section">
            <h2>Recent Admin Activity</h2>
            <div className="activity-list">
              {recentActivity.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-header">
                    <span className="activity-type">{activity.action_type}</span>
                    <span className="activity-time">
                      {new Date(activity.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="activity-description">
                    {activity.description}
                  </div>
                  <div className="activity-admin">
                    by {activity.admin_email}
                  </div>
                </div>
              ))}
            </div>
            <div className="activity-footer">
              <button 
                onClick={() => navigate('/admin/activity')} 
                className="view-all-button"
              >
                View All Activity →
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 